import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListFilters from '@/common/components/ListFilters'
import ListHeader from '@/common/components/ListHeader'
import Table from '@/common/components/Table'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { CompanyBranchData } from '@/common/types/companyBranches.type'
import { useAvailableCompanyBranches } from '@/features/companyBranches/hooks/useAvailableCompanyBranches'
import { useEstablishmentCompanies } from '@/features/companyBranches/hooks/useEstablishmentCompanies'
import { EstablishmentCompanyTransformer } from '@/features/companyBranches/services/establishmentCompanyTransformer'
import { failedTransactionsColumns } from '@/features/failedConsumptions/constants/failedConsumption.constant'
import { useConsumptions } from '@/features/failedConsumptions/hooks/useConsumptions'
import { FailedConsumptionsTransformer } from '@/features/failedConsumptions/services/failedConsumptionsTransformer'
import { ConsumptionData } from '@/features/failedConsumptions/types/consumption.type'
import { FailedConsumptionTableItem } from '@/features/failedConsumptions/types/failedConsumption.type'
import { ReceiptIcon } from 'lucide-react'

const FailedConsumptionsList = () => {
  const { filterParams, setFilterParams } = useFilterParams()

  const [companyBranchSearch, setCompanyBranchSearch] = useDebounce('')
  const [searchEstablishment, setSearchEstablishment] = useDebounce('')

  const { data: establishmentOptions } = useEstablishmentCompanies({
    filters: { search: searchEstablishment },
    mapper: Mapper.mapWithTransformer({ transformer: EstablishmentCompanyTransformer.optionTransformer() }),
  })

  const { data: clientCompanyBranchOptions } = useAvailableCompanyBranches({
    filters: { search: companyBranchSearch },
    mapper: Mapper.mapToOptions<CompanyBranchData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const {
    data: companyBillingEvents,
    meta,
    isFetching: isFetchingCompanyBillingEvents,
    error: companyBillingEventsError = {} as any,
    refetch: refetchCompanyBillingEvents,
  } = useConsumptions({
    filters: {
      status: 'failed',
      page: filterParams.page || 1,
      clientBranchId: filterParams.branch?.value,
      establishmentCompanyId: filterParams.establishmentCompany?.value,
    },
    mapper: Mapper.mapWithTransformer<ConsumptionData[], FailedConsumptionTableItem[]>({
      transformer: FailedConsumptionsTransformer.tableTransformer(),
    }),
  })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex">
        <ListHeader Icon={<ReceiptIcon className="w-10 h-10" strokeWidth={1.5} />} title="Transações falhas" />
      </div>

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={companyBillingEventsError?.status}
        refetchFunction={refetchCompanyBillingEvents}
      >
        <div className="flex flex-col gap-6">
          <ListFilters
            filters={[
              {
                className: 'min-w-56',
                onChange: (establishmentCompany) => setFilterParams((prev) => ({ ...prev, establishmentCompany })),
                value: filterParams.establishmentCompany,
                onInputChange: (value) => setSearchEstablishment(value),
                options: [{ label: 'Todas', value: '' }, ...(establishmentOptions || [])],
                placeholder: 'Estabelecimento',
              },
              {
                className: 'min-w-56',
                onChange: (branch) => setFilterParams((prev) => ({ ...prev, branch })),
                value: filterParams.branch,
                onInputChange: (e) => setCompanyBranchSearch(e),
                options: [{ label: 'Todas', value: '' }, ...(clientCompanyBranchOptions || [])],
                placeholder: 'Filial',
              },
            ]}
          />

          <Table
            className="max-h-[calc(100vh-184px)]"
            columns={failedTransactionsColumns}
            isLoadingData={isFetchingCompanyBillingEvents}
            page={filterParams.page || 1}
            rows={companyBillingEvents}
            totalPages={meta.total_pages}
            totalCount={meta.total}
            hasPagination
            rowEmptyValue="--"
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default FailedConsumptionsList
